/**
 * useBoothCameraPipeline ✦ iter198
 * ─────────────────────────────────────────────────────────────
 * Capture-grade infrastructure (NOT a media utility). One hook,
 * one vocabulary, every capture surface in the platform.
 *
 * Owns:
 *   • Profile-driven constraints (aspect ratio, resolution,
 *     facing mode, frame rate, audio).
 *   • Stream acquisition with recovery (permission retry, stream-
 *     interruption detection, Safari/Android quirks).
 *   • Mirror semantics — preview mirroring is separate from export
 *     mirroring (Lens Notes vs Booth OS).
 *   • Still capture with profile-consistent crop anchor + safe-zone
 *     framing (kills the "zoom" feeling that's actually poor crop
 *     anchoring on ultrawide / front-cam scaling).
 *   • Video recording with profile-driven mime negotiation +
 *     honest blob labelling.
 *   • Camera switching without hard black flashes (warm-stop).
 *   • Long-session resilience (tab background, bfcache, refresh).
 *
 * Deliberately NOT owned:
 *   • Cinematic choreography (Spin Film 360 sweep, photo-strip
 *     countdown). Consumers handle their own pacing.
 *   • Composition rendering (polaroid PNG, photo-strip composite).
 *
 * Usage:
 *
 *    const cam = useBoothCameraPipeline({ profile: 'booth' });
 *
 *    useEffect(() => { if (stage === 'live') cam.start(); }, [stage]);
 *
 *    <video ref={cam.videoRef} ... style={cam.previewStyle} />
 *
 *    // Still capture (returns a data URL honoring mirror semantics)
 *    const dataUrl = await cam.captureStill();
 *
 *    // Video record
 *    cam.startRecording();
 *    ...
 *    const { blob, mime } = await cam.stopRecording();
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import {
  getCameraProfile,
  profileToConstraints,
  shouldMirrorPreview,
  shouldMirrorExport,
} from '../lib/booth-os/boothCameraProfiles';
import FramingGuide from '../components/booth-os/FramingGuide';
import { emit as soakEmit } from '../lib/booth-os/soakTelemetry';

const SAFE_ZONE_PADDING_PX = 0; // safeZone is normalized; px is informational

/**
 * Apply a safe-zone crop to a canvas frame. profile.safeZone is in
 * normalized 0..1 coordinates of the source video. cropAnchor
 * decides which side anchors when the source ratio doesn't match
 * the profile's aspectRatio — almost always 'center'.
 */
function drawProfileFrame({ video, canvas, profile, mirror }) {
  const ctx = canvas.getContext('2d');
  const sw = video.videoWidth || profile.resolution.width;
  const sh = video.videoHeight || profile.resolution.height;
  const targetRatio = profile.aspectRatio;
  const srcRatio = sw / sh;

  // Cover-crop the source into the target aspect ratio so every
  // export from this profile has IDENTICAL framing geometry
  // regardless of which device the room is using. This is what
  // kills "zoom inconsistency" between Safari front-cam and
  // Android ultrawide.
  let cropW = sw;
  let cropH = sh;
  let cropX = 0;
  let cropY = 0;
  if (srcRatio > targetRatio) {
    cropW = sh * targetRatio;
    cropX = profile.cropAnchor === 'top' ? 0
      : profile.cropAnchor === 'bottom' ? sw - cropW
      : (sw - cropW) / 2;
  } else if (srcRatio < targetRatio) {
    cropH = sw / targetRatio;
    cropY = profile.cropAnchor === 'top' ? 0
      : profile.cropAnchor === 'bottom' ? sh - cropH
      : (sh - cropH) / 2;
  }

  // Apply the profile's normalized safeZone — chops a few percent
  // off the edges so the subject lands consistently in the centre
  // mass of the frame across device aspect ratios.
  const sz = profile.safeZone || { x: 0, y: 0, w: 1, h: 1 };
  const inX = cropX + cropW * sz.x;
  const inY = cropY + cropH * sz.y;
  const inW = cropW * sz.w;
  const inH = cropH * sz.h;

  canvas.width = Math.round(profile.resolution.width);
  canvas.height = Math.round(profile.resolution.width / targetRatio);

  ctx.save();
  if (mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(
    video,
    inX, inY, inW, inH,
    0, 0, canvas.width, canvas.height,
  );
  ctx.restore();
}

function negotiateVideoMime(candidates) {
  if (typeof window === 'undefined' || !window.MediaRecorder) return '';
  const list = Array.isArray(candidates) ? candidates : [];
  for (const cand of list) {
    try {
      if (window.MediaRecorder.isTypeSupported(cand)) return cand;
    } catch { /* some UAs throw on unknown candidates */ }
  }
  return '';
}

export function useBoothCameraPipeline({
  profile: profileName = 'booth',
  initialFacingMode,        // optional override (defaults to profile)
  onError,                  // optional error subscriber (toast/UI)
} = {}) {
  const profile = useMemo(() => getCameraProfile(profileName), [profileName]);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // 'idle' | 'requesting' | 'live' | 'recording' | 'denied' | 'error'
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState(
    initialFacingMode || profile.facingMode,
  );
  const [recordingMime, setRecordingMime] = useState('');
  // iter200 — Temporal framing stabilisation. Mobile browsers
  // settle aspect ratio LATE — iOS resizes after permission grant,
  // Android sometimes swaps width/height during rotation correction,
  // Safari renegotiates dimensions after the first paint. If the
  // FramingGuide fades in before these settle, the corner ticks
  // jump once — subconsciously damages perceived polish.
  //
  // We mark `dimensionsStable` true only after the video element
  // reports the SAME (width × height) on two consecutive animation
  // frames — i.e. the browser has stopped renegotiating the frame.
  // Falls back to `status === 'live'` + 800ms if rAF parity never
  // arrives (very old WebViews).
  const [dimensionsStable, setDimensionsStable] = useState(false);

  // Pure-function helpers exposed so consumers can render mirror-
  // matching review surfaces without needing to know the rules.
  const mirrorPreview = shouldMirrorPreview(profile, facingMode);
  const mirrorExport = shouldMirrorExport(profile, facingMode);

  const reportError = useCallback((msg, err) => {
    setError(msg);
    if (typeof onError === 'function') {
      try { onError(msg, err); } catch { /* swallow */ }
    }
  }, [onError]);

  // iter197 — Self-healing srcObject attachment.
  //
  // The original contract assumed videoRef.current was non-null at
  // the moment getUserMedia resolved. Reality on mobile: when a
  // consumer calls cam.start() inside the same tick as setStep(
  // 'capture'), the <video ref={videoRef}> element doesn't exist
  // yet — React hasn't committed the new tree. getUserMedia then
  // resolves before the commit, the if(videoRef.current) branch
  // skips, and the stream is acquired but never attached. User
  // sees the capture surface with no preview.
  //
  // This effect fires whenever `status` flips or `videoRef.current`
  // becomes truthy (via the next render). If a stream exists and
  // hasn't been attached yet, attach it now. Idempotent — guards
  // against re-attaching the same stream.
  useEffect(() => {
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject === s) return;
    try {
      v.srcObject = s;
      v.muted = true;
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* iOS gesture race — retried by next status tick */ });
      }
    } catch { /* DOM teardown race — next render will retry */ }
  }, [status]);

  const stop = useCallback(() => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); }
      catch { /* hardware races */ }
      streamRef.current = null;
      soakEmit('cam:stop', { profile: profileName });
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* DOM races */ }
    }
    setStatus((s) => (s === 'recording' ? 'live' : s === 'live' ? 'idle' : s));
  }, [profileName]);

  const start = useCallback(async (override = {}) => {
    // Warm-stop the previous stream first — but keep status='live'
    // visible to the consumer until the new stream actually paints
    // (warm-start contract — "avoid hard black flashes where
    // possible"). The old <video> keeps its last frame on-screen
    // until we assign the new srcObject.
    const hadStream = !!streamRef.current;
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
      streamRef.current = null;
    }
    if (!hadStream) setStatus('requesting');
    setError(null);

    const nextFacing = override.facingMode || facingMode;
    if (override.facingMode && override.facingMode !== facingMode) {
      setFacingMode(override.facingMode);
    }
    const constraints = profileToConstraints(profile, { facingMode: nextFacing });

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // iter207 — soak telemetry. No-op in production unless the
      // /debug/soak-test page has explicitly enabled the collector.
      soakEmit(hadStream ? 'cam:restart' : 'cam:start', { profile: profileName, facingMode: nextFacing });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        try { await videoRef.current.play(); }
        catch { /* iOS may race the gesture — ignored, browser retries on next tick */ }
      }
      // Stream-interruption recovery — if the OS suspends the
      // track (iOS lock screen, Android background), surface that
      // so the consumer can re-prompt the user gracefully.
      stream.getVideoTracks().forEach((t) => {
        t.onended = () => {
          soakEmit('cam:track-ended', { profile: profileName });
          if (streamRef.current === stream) {
            reportError(
              'The camera was paused by the device. Tap the screen to bring it back.',
            );
            setStatus('idle');
          }
        };
      });
      setStatus('live');
      return stream;
    } catch (err) {
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
      setStatus(denied ? 'denied' : 'error');
      reportError(
        denied
          ? 'Camera access is blocked. Open the booth permissions and allow the camera, then try again.'
          : 'We couldn’t reach the camera. Check the booth permissions.',
        err,
      );
      return null;
    }
  }, [profile, facingMode, reportError]);

  const switchCamera = useCallback(() => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    return start({ facingMode: next });
  }, [facingMode, start]);

  /**
   * Capture a still frame as a data URL, honoring mirror semantics
   * and the profile's safeZone. Returns null if the stream isn't
   * live — never returns a half-baked frame.
   */
  const captureStill = useCallback(async ({
    mime, quality,
  } = {}) => {
    const v = videoRef.current;
    if (!v || !streamRef.current) return null;
    if (!v.videoWidth || !v.videoHeight) return null;
    const canvas = document.createElement('canvas');
    drawProfileFrame({ video: v, canvas, profile, mirror: mirrorExport });
    return canvas.toDataURL(mime || profile.exportMime, quality ?? profile.exportQuality);
  }, [profile, mirrorExport]);

  /**
   * Capture a still as a Blob — convenience for surfaces that go
   * directly to FormData (no intermediate dataURL round-trip).
   */
  const captureStillBlob = useCallback(async ({
    mime, quality,
  } = {}) => {
    const v = videoRef.current;
    if (!v || !streamRef.current || !v.videoWidth || !v.videoHeight) return null;
    const canvas = document.createElement('canvas');
    drawProfileFrame({ video: v, canvas, profile, mirror: mirrorExport });
    return new Promise((resolve) => {
      canvas.toBlob(
        (b) => resolve(b),
        mime || profile.exportMime,
        quality ?? profile.exportQuality,
      );
    });
  }, [profile, mirrorExport]);

  /**
   * Start recording video. Requires the profile to have audio:true
   * (i.e. the guestbook profile). The negotiated mime is exposed
   * via `recordingMime` so the consumer can label the resulting
   * blob honestly.
   */
  const startRecording = useCallback(() => {
    if (!streamRef.current) return false;
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      reportError('This browser can’t record on its own. Try the latest Chrome or Safari.');
      return false;
    }
    chunksRef.current = [];
    const mime = negotiateVideoMime(profile.videoMimeCandidates);
    setRecordingMime(mime);
    try {
      const opts = { ...(mime ? { mimeType: mime } : {}) };
      if (profile.videoBitsPerSecond) opts.videoBitsPerSecond = profile.videoBitsPerSecond;
      const rec = new window.MediaRecorder(streamRef.current, opts);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.start(250);
      setStatus('recording');
      soakEmit('cam:rec-start', { profile: profileName, mime });
      return true;
    } catch (err) {
      reportError('The recorder didn’t open. Try once more, slowly.', err);
      return false;
    }
  }, [profile, reportError]);

  /**
   * Stop recording. Returns a Promise resolving with
   * { blob, mime, ext } — `ext` is a download-friendly extension
   * derived from the recorder's negotiated mime (NOT the candidate
   * list, so an iOS Safari fallback to mp4 lands as .mp4).
   */
  const stopRecording = useCallback(() => new Promise((resolve) => {
    const rec = recorderRef.current;
    if (!rec) return resolve({ blob: null, mime: '', ext: '' });
    if (rec.state === 'inactive') {
      const mime = rec.mimeType || recordingMime || 'video/webm';
      return resolve({
        blob: new Blob(chunksRef.current, { type: mime }),
        mime,
        ext: mime.includes('mp4') ? 'mp4' : 'webm',
      });
    }
    rec.onstop = () => {
      // The recorder's *actual* mimeType — not the requested one —
      // so the blob label is honest about what bytes it carries.
      const mime = rec.mimeType || recordingMime || 'video/webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      soakEmit('cam:rec-stop', { profile: profileName, mime, bytes: blob.size });
      setStatus((s) => (s === 'recording' ? 'live' : s));
      resolve({
        blob,
        mime,
        ext: mime.includes('mp4') ? 'mp4' : 'webm',
      });
    };
    try { rec.stop(); }
    catch { resolve({ blob: null, mime: '', ext: '' }); }
  }), [recordingMime]);

  // ── Long-session resilience (folds in useMediaStreamCleanup
  //    behaviour so consumers don't have to wire both hooks). ─
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) stop(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', stop);
    window.addEventListener('beforeunload', stop);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('beforeunload', stop);
      stop();
    };
  }, [stop]);

  // iter200 — Dimensions-stable watcher. While the stream is live
  // we sample videoWidth × videoHeight on each animation frame and
  // flip `dimensionsStable` true only when two consecutive frames
  // report identical non-zero values. This is what tells the
  // FramingGuide it's safe to fade in without a one-frame jump.
  //
  // A hard 800ms safety net flips the flag true regardless — very
  // old WebView builds never report stable dimensions through the
  // <video> element's intrinsic size API; we'd rather show the
  // guide on those than silently never show it.
  useEffect(() => {
    if (status !== 'live') {
      setDimensionsStable(false);
      return undefined;
    }
    let rafId = 0;
    let prev = '0×0';
    let parity = 0;
    const fallback = setTimeout(() => setDimensionsStable(true), 800);
    const tick = () => {
      const v = videoRef.current;
      if (!v) { rafId = requestAnimationFrame(tick); return; }
      const sig = `${v.videoWidth}×${v.videoHeight}`;
      if (sig !== '0×0' && sig === prev) {
        parity += 1;
        if (parity >= 1) {
          setDimensionsStable(true);
          clearTimeout(fallback);
          return;
        }
      } else {
        parity = 0;
      }
      prev = sig;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(fallback);
    };
  }, [status]);

  // Re-attach stream to a freshly-mounted <video> node — the
  // intro→record transition often unmounts the intro video and
  // mounts a fresh one inside the recording branch. Without this
  // reattach the recording stage shows a black preview.
  useEffect(() => {
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject === s) return;
    v.srcObject = s;
    v.muted = true;
    v.play().catch(() => { /* iOS race — browser retries */ });
  }, [status]);

  // Consumer-friendly preview style. Encapsulates the mirror
  // decision so no surface re-implements `transform: scaleX(-1)`.
  const previewStyle = useMemo(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: mirrorPreview ? 'scaleX(-1)' : 'none',
    transition: 'opacity 280ms ease',
  }), [mirrorPreview]);

  // iter199 — Profile-bound framing guide. Consumer renders this
  // as a sibling of the <video> inside the camera frame:
  //
  //   <div ref={frameRef} style={{ position: 'relative' }}>
  //     <video ref={cam.videoRef} style={cam.previewStyle} />
  //     <cam.FramingGuide longPressTarget={frameRef} />
  //   </div>
  //
  // The guide reads its profile from this closure so consumers
  // never pass it again — no opportunity for profile drift.
  //
  // iter200 — `streamReady` is forwarded from the pipeline's
  // dimensionsStable watcher so the guide never fades in before
  // the browser settles videoWidth × videoHeight. Prevents the
  // "one-frame jump" on iOS Safari / Android rotation correction.
  const BoundFramingGuide = useCallback(
    (props) => (
      <FramingGuide
        profile={profile}
        streamReady={dimensionsStable}
        {...props}
      />
    ),
    [profile, dimensionsStable],
  );

  return {
    // refs
    videoRef,
    streamRef,

    // state
    status,       // 'idle' | 'requesting' | 'live' | 'recording' | 'denied' | 'error'
    error,
    facingMode,
    recordingMime,
    profile,      // the resolved frozen profile config
    mirrorPreview,
    mirrorExport,
    previewStyle, // ready-to-spread <video style={previewStyle}>
    dimensionsStable, // iter200 — true once videoWidth×videoHeight settles

    // actions
    start,
    stop,
    switchCamera,
    captureStill,
    captureStillBlob,
    startRecording,
    stopRecording,

    // composition (iter199)
    FramingGuide: BoundFramingGuide,
  };
}

export default useBoothCameraPipeline;
